// Admin component for viewing error logs and managing alert configurations

import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/components/ui/use-toast';
import { Database } from '../../../database.types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Trash2, Plus, AlertCircle } from 'lucide-react';

type ErrorLog = Database['public']['Tables']['error_logs']['Row'];
type AlertConfiguration = Database['public']['Tables']['alert_configurations']['Row'];

export default function ErrorLogsManagement() {
  const [errorLogs, setErrorLogs] = useState<ErrorLog[]>([]);
  const [alertConfigs, setAlertConfigs] = useState<AlertConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSeverity, setSelectedSeverity] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const { toast } = useToast();

  // New alert configuration form state
  const [newConfig, setNewConfig] = useState({
    name: '',
    channel: 'email' as 'email' | 'slack' | 'discord' | 'webhook',
    enabled: true,
    severity_threshold: 'high' as 'low' | 'medium' | 'high' | 'critical',
    config: {} as Record<string, any>,
  });

  useEffect(() => {
    fetchErrorLogs();
    fetchAlertConfigurations();
  }, [selectedSeverity, selectedCategory]);

  const fetchErrorLogs = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('error_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (selectedSeverity !== 'all') {
        query = query.eq('severity', selectedSeverity);
      }
      if (selectedCategory !== 'all') {
        query = query.eq('category', selectedCategory);
      }

      const { data, error } = await query;

      if (error) throw error;
      setErrorLogs(data || []);
    } catch (error) {
      console.error('Error fetching error logs:', error);
      toast({
        title: "Error",
        description: "Failed to fetch error logs",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAlertConfigurations = async () => {
    try {
      const { data, error } = await supabase
        .from('alert_configurations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAlertConfigs(data || []);
    } catch (error) {
      console.error('Error fetching alert configurations:', error);
      toast({
        title: "Error",
        description: "Failed to fetch alert configurations",
        variant: "destructive",
      });
    }
  };

  const createAlertConfiguration = async () => {
    try {
      // Validate configuration based on channel type
      let config = {};
      switch (newConfig.channel) {
        case 'email':
          config = {
            to: [newConfig.config.to],
            from: newConfig.config.from || 'alerts@rainfactory.ai'
          };
          break;
        case 'slack':
        case 'discord':
          config = {
            webhook_url: newConfig.config.webhook_url
          };
          break;
        case 'webhook':
          config = {
            url: newConfig.config.url,
            headers: newConfig.config.headers || {}
          };
          break;
      }

      const { error } = await supabase
        .from('alert_configurations')
        .insert({
          name: newConfig.name,
          channel: newConfig.channel,
          enabled: newConfig.enabled,
          severity_threshold: newConfig.severity_threshold,
          config,
          categories: [],
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Alert configuration created successfully",
      });

      // Reset form and refresh
      setNewConfig({
        name: '',
        channel: 'email',
        enabled: true,
        severity_threshold: 'high',
        config: {},
      });
      fetchAlertConfigurations();
    } catch (error) {
      console.error('Error creating alert configuration:', error);
      toast({
        title: "Error",
        description: "Failed to create alert configuration",
        variant: "destructive",
      });
    }
  };

  const deleteAlertConfiguration = async (id: string) => {
    try {
      const { error } = await supabase
        .from('alert_configurations')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Alert configuration deleted successfully",
      });
      fetchAlertConfigurations();
    } catch (error) {
      console.error('Error deleting alert configuration:', error);
      toast({
        title: "Error",
        description: "Failed to delete alert configuration",
        variant: "destructive",
      });
    }
  };

  const getSeverityBadgeVariant = (severity: string | null) => {
    switch (severity) {
      case 'critical': return 'destructive';
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'outline';
    }
  };

  const getCategoryBadgeVariant = (category: string | null) => {
    switch (category) {
      case 'authentication':
      case 'authorization': return 'destructive';
      case 'external_api':
      case 'network': return 'default';
      default: return 'secondary';
    }
  };

  return (
    <Tabs defaultValue="logs" className="w-full">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="logs">Error Logs</TabsTrigger>
        <TabsTrigger value="alerts">Alert Configurations</TabsTrigger>
      </TabsList>

      <TabsContent value="logs">
        <Card>
          <CardHeader>
            <CardTitle>Error Logs</CardTitle>
            <CardDescription>View and filter system error logs</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 mb-4">
              <Select value={selectedSeverity} onValueChange={setSelectedSeverity}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Severities</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>

              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="authentication">Authentication</SelectItem>
                  <SelectItem value="authorization">Authorization</SelectItem>
                  <SelectItem value="validation">Validation</SelectItem>
                  <SelectItem value="external_api">External API</SelectItem>
                  <SelectItem value="database">Database</SelectItem>
                  <SelectItem value="business_logic">Business Logic</SelectItem>
                  <SelectItem value="system">System</SelectItem>
                  <SelectItem value="network">Network</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="text-center py-4">Loading...</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Severity</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Error</TableHead>
                      <TableHead>Worker</TableHead>
                      <TableHead>Alert Sent</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {errorLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="text-sm">
                          {new Date(log.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Badge variant={getSeverityBadgeVariant(log.severity)}>
                            {log.severity || 'unknown'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getCategoryBadgeVariant(log.category)}>
                            {log.category || 'unknown'}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-md">
                          <div className="truncate" title={log.error_message}>
                            {log.error_message}
                          </div>
                        </TableCell>
                        <TableCell>{log.worker_name || 'N/A'}</TableCell>
                        <TableCell>
                          {log.alert_sent ? (
                            <Badge variant="outline">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Sent
                            </Badge>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="alerts">
        <Card>
          <CardHeader>
            <CardTitle>Alert Configurations</CardTitle>
            <CardDescription>Configure how and when alerts are sent</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Create new alert configuration */}
              <div className="border rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-4">Create New Alert Configuration</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Configuration Name</Label>
                    <Input
                      id="name"
                      value={newConfig.name}
                      onChange={(e) => setNewConfig({ ...newConfig, name: e.target.value })}
                      placeholder="e.g., Critical Errors to Slack"
                    />
                  </div>
                  <div>
                    <Label htmlFor="channel">Alert Channel</Label>
                    <Select
                      value={newConfig.channel}
                      onValueChange={(value: any) => setNewConfig({ ...newConfig, channel: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="slack">Slack</SelectItem>
                        <SelectItem value="discord">Discord</SelectItem>
                        <SelectItem value="webhook">Webhook</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="severity">Minimum Severity</Label>
                    <Select
                      value={newConfig.severity_threshold}
                      onValueChange={(value: any) => setNewConfig({ ...newConfig, severity_threshold: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Channel-specific configuration */}
                  {newConfig.channel === 'email' && (
                    <div>
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        value={newConfig.config.to || ''}
                        onChange={(e) => setNewConfig({
                          ...newConfig,
                          config: { ...newConfig.config, to: e.target.value }
                        })}
                        placeholder="admin@example.com"
                      />
                    </div>
                  )}
                  
                  {(newConfig.channel === 'slack' || newConfig.channel === 'discord') && (
                    <div>
                      <Label htmlFor="webhook">Webhook URL</Label>
                      <Input
                        id="webhook"
                        value={newConfig.config.webhook_url || ''}
                        onChange={(e) => setNewConfig({
                          ...newConfig,
                          config: { ...newConfig.config, webhook_url: e.target.value }
                        })}
                        placeholder="https://hooks.slack.com/..."
                      />
                    </div>
                  )}
                  
                  {newConfig.channel === 'webhook' && (
                    <div>
                      <Label htmlFor="url">Webhook URL</Label>
                      <Input
                        id="url"
                        value={newConfig.config.url || ''}
                        onChange={(e) => setNewConfig({
                          ...newConfig,
                          config: { ...newConfig.config, url: e.target.value }
                        })}
                        placeholder="https://example.com/webhook"
                      />
                    </div>
                  )}
                </div>
                <Button
                  onClick={createAlertConfiguration}
                  className="mt-4"
                  disabled={!newConfig.name}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Configuration
                </Button>
              </div>

              {/* Existing configurations */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Existing Configurations</h3>
                <div className="space-y-4">
                  {alertConfigs.map((config) => (
                    <Alert key={config.id}>
                      <AlertDescription className="flex justify-between items-center">
                        <div>
                          <strong>{config.name}</strong>
                          <span className="ml-4">
                            Channel: <Badge variant="outline">{config.channel}</Badge>
                          </span>
                          <span className="ml-4">
                            Threshold: <Badge variant={getSeverityBadgeVariant(config.severity_threshold)}>
                              {config.severity_threshold}
                            </Badge>
                          </span>
                          <span className="ml-4">
                            Status: <Badge variant={config.enabled ? "default" : "secondary"}>
                              {config.enabled ? 'Enabled' : 'Disabled'}
                            </Badge>
                          </span>
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => deleteAlertConfiguration(config.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDescription>
                    </Alert>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}